import {
  ReactElement, useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { t } from 'i18next';

import { DONUT_COLORS, DONUT_OTHER_COLOR } from '../../config';
import { DonutTier } from '../../tier';
import { DonutLegend } from './parts/DonutLegend';
import { DonutSkeleton } from './parts/DonutSkeleton';
import { aggregateTopN } from './helpers';
import { shouldRenderHtmlLegend } from './layout';
import { Card, ChartArea, Title } from './styles';
import { ChartDatum, DonutItem, DonutUnit } from './types';
import { useGenerateDonutChart } from './useGenerateDonutChart';

export type { DonutItem } from './types';

interface DonutCardProps {
  title: string;
  unit: DonutUnit;
  items: DonutItem[] | undefined;
  loading: boolean;
  tier: DonutTier;
}

export function DonutCard({
  title,
  unit,
  items,
  loading,
  tier,
}: Readonly<DonutCardProps>): ReactElement {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const [hidden, setHidden] = useState<Set<string>>(new Set());

  const data = useMemo<ChartDatum[]>(
    () => (items ? aggregateTopN(items) : []),
    [items],
  );

  const colors = useMemo<string[]>(
    () => data.map((d, idx) => (
      d.name === t('outros')
        ? DONUT_OTHER_COLOR
        : DONUT_COLORS[idx % DONUT_COLORS.length]
    )),
    [data],
  );

  const handleLegendChange = useCallback((selected: Record<string, boolean>) => {
    const next = new Set<string>();
    Object.entries(selected).forEach(([name, isSelected]) => {
      if (!isSelected) next.add(name);
    });
    setHidden(next);
  }, []);

  const { renderChart, toggleLegendItem } = useGenerateDonutChart({
    containerRef: chartContainerRef,
    onLegendChange: handleLegendChange,
  });

  useEffect(() => {
    setHidden(new Set());
  }, [items]);

  useEffect(() => {
    if (loading || !items) return;
    renderChart({
      data, colors, unit, hidden, tier,
    });
  }, [loading, items, data, colors, unit, hidden, tier, renderChart]);

  const isLoading = loading || !items;
  const showHtmlLegend = !isLoading && shouldRenderHtmlLegend(tier);

  return (
    <Card $tier={tier}>
      <Title>{title}</Title>
      {isLoading ? (
        <DonutSkeleton tier={tier} />
      ) : (
        <>
          <ChartArea $tier={tier} ref={chartContainerRef} />
          {showHtmlLegend && (
            <DonutLegend
              data={data}
              colors={colors}
              hidden={hidden}
              onToggle={toggleLegendItem}
            />
          )}
        </>
      )}
    </Card>
  );
}
